/**
 * GdsThemePreview - GDS 配色模板本地验收页（仅开发预览用，不在正式导航中暴露）
 *
 * 访问：http://localhost:3005/?preview=gds-themes
 * 作用：把 4 款 GDS 配色并排渲染，肉眼比对页头 / 卡片 / 表格 / 按钮的换肤效果。
 *      使用的组件与正式渲染链路完全一致（LabelCanvas），因此看到的就是真实效果。
 */

import React, { useState } from 'react';
import LabelCanvas from './LabelView/LabelCanvas';
import TemplateSelector from '../components/modals/TemplateSelector';
import { GDS_THEMES, GDS_VARIANTS, getGdsHeaderStyle } from '../config/gdsThemes';
import './styles/GdsThemePreview.css';

// 一个图片占位（内联 SVG，避免依赖外网）
const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="720" height="480">
      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#e9edf3"/><stop offset="100%" stop-color="#cfd8e3"/>
      </linearGradient></defs>
      <rect width="720" height="480" fill="url(#g)"/>
      <circle cx="360" cy="196" r="62" fill="#ffffff" opacity="0.75"/>
      <rect x="300" y="176" width="120" height="10" rx="5" fill="#a9b6c6"/>
      <rect x="286" y="200" width="148" height="10" rx="5" fill="#c3cdd9"/>
      <text x="360" y="330" font-family="Microsoft YaHei, sans-serif" font-size="26"
            fill="#7b8899" text-anchor="middle">产品图占位</text>
    </svg>`
  );

/** 构造一份示意标签数据，字段结构与真实标签保持一致 */
function buildMockLabel(variant) {
  const theme = GDS_THEMES[variant];
  return {
    id: `preview-${variant}`,
    labelName: '示例产品数字标签',
    backgroundColor: theme.paper,
    contentItems: [
      {
        contentType: 'background',
        contentData: { color: theme.paper },
      },
      {
        contentType: 'image',
        contentData: {
          images: [{ src: PLACEHOLDER_IMAGE, width: 100 }],
          description: '',
        },
      },
      {
        contentType: 'product-basic',
        contentData: {
          productName: '示例产品 · 原味',
          ingredients: '水、全脂乳粉、白砂糖、食品添加剂（柠檬酸）',
          netContent: '250mL',
          standardCode: 'GB 25191',
          manufacturer: '示例食品有限公司',
          address: '陕西省西安市某某区某某路 1 号',
          origin: '陕西省西安市',
          productionDate: '2026-11-03',
          shelfLife: '12 个月',
          storageConditions: '常温阴凉干燥处存放，避免阳光直射',
        },
      },
      {
        contentType: 'nutrition',
        contentData: {
          // 「每100克」可编辑：数值 + 单位
          basis: { value: 100, unit: '克' },
          items: [
            { name: '能量', per100g: '268', unit: 'kJ', nrv: '3%' },
            { name: '蛋白质', per100g: '3.2', unit: 'g', nrv: '5%' },
            { name: '脂肪', per100g: '3.6', unit: 'g', nrv: '6%' },
            { name: '碳水化合物', per100g: '4.5', unit: 'g', nrv: '2%' },
            { name: '钠', per100g: '52', unit: 'mg', nrv: '3%' },
          ],
        },
      },
      {
        contentType: 'usage',
        contentData: {
          title: '直接饮用，冷藏后风味更佳；开启后请尽快饮用完毕。',
        },
      },
      {
        contentType: 'inspection',
        contentData: {
          attachments: [
            { name: '出厂检验报告', file: { url: '#' }, enabled: true },
          ],
        },
      },
      {
        contentType: 'related-links',
        contentData: {
          layout: 'vertical-card',
          links: [
            { name: '官方网站', url: '#', order: 1, enabled: true },
            { name: '追溯查询', url: '#', order: 2, enabled: true },
          ],
        },
      },
    ],
    meta: {
      templateVariant: variant,
      headerVisible: true,
      footerText: '技术支持：数字标签平台',
      decorations: { enabled: false },
      watermark: { enabled: false },
      layout: { blockGap: 14 },
      headerStyle: getGdsHeaderStyle(variant),
    },
  };
}

export default function GdsThemePreview() {
  // 支持 ?preview=gds-themes&modal=1 直接打开模板选择器，免登录验收新增卡片
  const [showSelector, setShowSelector] = useState(
    window.location.search.includes('modal=1')
  );

  return (
    <div className="gds-preview-page">
      <header className="gds-preview-head">
        <h1>GDS 配色模板 · 本地验收</h1>
        <p>
          4 款配色共用同一套标签骨架（标准食品标签布局），仅替换配色令牌。下面是
          <b>真实渲染链路</b>的输出，可直接对照设计稿核对页头、卡片、表格与按钮。
        </p>
        <button type="button" className="gds-preview-open" onClick={() => setShowSelector(true)}>
          打开模板选择器（看新增的 4 张卡片）
        </button>
      </header>

      {showSelector && (
        <TemplateSelector
          isOpen={showSelector}
          onClose={() => setShowSelector(false)}
          onSelect={(sel) => console.log('[预览] 模板选择结果:', sel)}
        />
      )}

      <div className="gds-preview-grid">
        {GDS_VARIANTS.map((variant) => {
          const theme = GDS_THEMES[variant];
          return (
            <section className="gds-preview-card" key={variant}>
              <div className="gds-preview-badge">
                <span className="gds-preview-dot" style={{ background: theme.brand }} />
                <span className="gds-preview-name">{theme.name}</span>
                <code>{theme.brand}</code>
              </div>
              <div className="gds-preview-stage">
                <LabelCanvas data={buildMockLabel(variant)} phonePreview />
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
